import { AxiosError } from 'axios';
import API from '../../utils/axios.utils';
import { IContainer, IContainerCreate, IContainerType, ILengthUOM } from '../model';

// Helper function to get auth token from sessionStorage
function getAuthToken(): string | null {
  return sessionStorage.getItem('accessToken');
}

// Helper function to get auth headers
function getAuthHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// API endpoints
const API_PREFIX = 'api/user/edgelink';
const TANK_CONFIGS_URL = `${API_PREFIX}/tank-configs`;
const TANK_TYPES_URL = `${API_PREFIX}/tank-types`;
const SENSOR_UOMS_URL = `${API_PREFIX}/sensor-uoms`;
const DEVICES_URL = `${API_PREFIX}/devices`;
const TANK_VOLUMES_URL = `${API_PREFIX}/tank-volumes/calculate/`;

// Cache and debounce configuration
interface CacheItem<T> {
  data: T;
  timestamp: number;
}

const cache: {
  [key: string]: CacheItem<any>;
} = {};

const CACHE_TTL = 10000; // Cache time to live in milliseconds (10 seconds)
const pendingRequests: {
  [key: string]: Promise<any>;
} = {};

const lastRequestTime: {
  [key: string]: number;
} = {};
const THROTTLE_INTERVAL = 2000; // Minimum time between requests in milliseconds (2 seconds)

/**
 * Get all containers
 * @param includeInactive Whether to include inactive containers in results
 * @returns Promise resolving to filtered array of containers
 */
export async function getAllContainers(includeInactive = false): Promise<IContainer[]> {
  const cacheKey = `containers_${includeInactive}`;
  const now = Date.now();

  // Return cached data if it's still valid
  if (cache[cacheKey] && now - cache[cacheKey].timestamp < CACHE_TTL) {
    console.log('Returning cached container data');
    return cache[cacheKey].data as IContainer[];
  }

  // If there's already a pending request for this data, return that instead of making a new one
  if (pendingRequests[cacheKey]) {
    console.log('Returning pending container request');
    return pendingRequests[cacheKey];
  }

  // Throttle requests to ensure minimum interval between them
  const lastRequest = lastRequestTime[cacheKey] || 0;
  if (now - lastRequest < THROTTLE_INTERVAL) {
    console.log('Throttling container request');

    // If we need to throttle, return cached data even if expired, or wait and proceed
    if (cache[cacheKey]) {
      return cache[cacheKey].data as IContainer[];
    }

    // If no cache data exists, wait for the remaining throttle time
    const waitTime = THROTTLE_INTERVAL - (now - lastRequest);
    await new Promise((resolve) => setTimeout(resolve, waitTime));
  }

  // Track the last request time
  lastRequestTime[cacheKey] = Date.now();

  try {
    // Build query params
    const params: Record<string, string> = {};
    if (includeInactive) {
      params.includeInactive = 'true';
    }

    // Store the promise to prevent duplicate in-flight requests
    pendingRequests[cacheKey] = API.get(TANK_CONFIGS_URL, {
      params,
      headers: getAuthHeaders(),
    })
      .then((response) => {
        const data = response.data as IContainer[];

        // Update cache
        cache[cacheKey] = {
          data,
          timestamp: Date.now(),
        };

        // Clear the pending request reference
        delete pendingRequests[cacheKey];

        return data;
      })
      .catch((error) => {
        // Clear the pending request reference on error
        delete pendingRequests[cacheKey];
        console.error('Error fetching containers:', error);
        throw error;
      });

    return await pendingRequests[cacheKey];
  } catch (error) {
    console.error('Error setting up container request:', error);
    throw error;
  }
}

/**
 * Add a new container
 * @param container Container data to add
 * @returns Promise resolving to the added container with ID
 */
export async function addContainer(container: IContainerCreate): Promise<IContainer> {
  try {
    // Validate required fields
    if (
      !container.tank_name ||
      !container.edgelink_tank_type_id ||
      !container.distance_uom_id ||
      !container.dimensions
    ) {
      throw new Error('Missing required container fields');
    }

    // Make API request
    const response = await API.post(`${TANK_CONFIGS_URL}/`, container, {
      headers: getAuthHeaders(),
    });

    // Clear the containers cache when adding a new container
    Object.keys(cache).forEach((key) => {
      if (key.startsWith('containers_')) {
        delete cache[key];
      }
    });

    return response.data as unknown as IContainer;
  } catch (error) {
    console.error('Error creating container:', error);
    throw error;
  }
}

/**
 * Update an existing container
 * @param containerId ID of container to update
 * @param updates Partial container object with fields to update
 * @returns Promise resolving to number of affected rows (1 for success, 0 for not found)
 */
export async function updateContainer(containerId: number, updates: Partial<IContainer>): Promise<number> {
  try {
    // Make API request
    try {
      const response = await API.put(`${TANK_CONFIGS_URL}/${containerId}/`, updates, {
        headers: getAuthHeaders(),
      });

      // Clear the containers cache when updating a container
      Object.keys(cache).forEach((key) => {
        if (key.startsWith('containers_')) {
          delete cache[key];
        }
      });

      // Return 1 to indicate success (1 row affected)
      return 1;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response && axiosError.response.status === 404) {
        // Container not found
        return 0;
      }
      throw error;
    }
  } catch (error) {
    console.error(`Error updating container ${containerId}:`, error);
    throw error;
  }
}

/**
 * Get a container by ID
 * @param containerId ID of container to retrieve
 * @returns Promise resolving to container or null if not found
 */
export async function getContainerById(containerId: number): Promise<IContainer | null> {
  const cacheKey = `container_${containerId}`;
  const now = Date.now();

  // Return cached data if it's still valid
  if (cache[cacheKey] && now - cache[cacheKey].timestamp < CACHE_TTL) {
    console.log(`Returning cached container ${containerId}`);
    return cache[cacheKey].data as IContainer;
  }

  // If there's already a pending request for this data, return that instead of making a new one
  if (pendingRequests[cacheKey]) {
    console.log(`Returning pending request for container ${containerId}`);
    return pendingRequests[cacheKey];
  }

  try {
    // Make API request
    pendingRequests[cacheKey] = (async () => {
      try {
        const response = await API.get(`${TANK_CONFIGS_URL}/${containerId}`, {
          headers: getAuthHeaders(),
        });

        const data = response.data as IContainer;

        // Update cache
        cache[cacheKey] = {
          data,
          timestamp: Date.now(),
        };

        return data;
      } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response && axiosError.response.status === 404) {
          return null;
        }
        throw error;
      } finally {
        // Clear the pending request reference
        delete pendingRequests[cacheKey];
      }
    })();

    return await pendingRequests[cacheKey];
  } catch (error) {
    console.error(`Error fetching container ${containerId}:`, error);
    throw error;
  }
}

/**
 * Delete a container by ID
 * @param containerId ID of container to delete
 * @returns Promise resolving to boolean indicating success
 */
export async function deleteContainer(containerId: number): Promise<boolean> {
  try {
    // Check if system default container (can't be deleted)
    const container = await getContainerById(containerId);
    if (!container) {
      return false;
    }

    if (container.is_system_default) {
      console.warn(`Container ${containerId} is a system default and cannot be deleted`);
      return false;
    }

    // Make API request
    try {
      await API.delete(`${TANK_CONFIGS_URL}/${containerId}/`, {
        headers: getAuthHeaders(),
      });

      // Clear the containers cache when deleting a container
      Object.keys(cache).forEach((key) => {
        if (key.startsWith('containers_') || key === `container_${containerId}`) {
          delete cache[key];
        }
      });

      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response && (axiosError.response.status === 204 || axiosError.response.status === 200)) {
        return true;
      }
      throw error;
    }
  } catch (error) {
    console.error(`Error deleting container ${containerId}:`, error);
    throw error;
  }
}

/**
 * Get all tank types with their required dimensions
 * @returns Promise resolving to array of tank types
 */
export async function getTankTypes(): Promise<IContainerType[]> {
  const cacheKey = 'tank_types';
  const now = Date.now();

  // Return cached data if it's still valid
  if (cache[cacheKey] && now - cache[cacheKey].timestamp < CACHE_TTL) {
    console.log('Returning cached tank types');
    return cache[cacheKey].data as IContainerType[];
  }

  // If there's already a pending request for this data, return that instead of making a new one
  if (pendingRequests[cacheKey]) {
    console.log('Returning pending tank types request');
    return pendingRequests[cacheKey];
  }

  try {
    // Make API request
    pendingRequests[cacheKey] = API.get(TANK_TYPES_URL, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        const data = response.data as IContainerType[];

        // Update cache
        cache[cacheKey] = {
          data,
          timestamp: Date.now(),
        };

        // Clear the pending request reference
        delete pendingRequests[cacheKey];

        return data;
      })
      .catch((error) => {
        // Clear the pending request reference on error
        delete pendingRequests[cacheKey];
        console.error('Error fetching tank types:', error);
        throw error;
      });

    return await pendingRequests[cacheKey];
  } catch (error) {
    console.error('Error fetching tank types:', error);
    throw error;
  }
}

/**
 * Get distance UOMs for tank measurements
 * @returns Promise resolving to array of distance UOMs
 */
export async function getDistanceUOMs(): Promise<ILengthUOM[]> {
  const cacheKey = 'distance_uoms';
  const now = Date.now();

  // Return cached data if it's still valid
  if (cache[cacheKey] && now - cache[cacheKey].timestamp < CACHE_TTL) {
    console.log('Returning cached distance UOMs');
    return cache[cacheKey].data as ILengthUOM[];
  }

  // If there's already a pending request for this data, return that instead of making a new one
  if (pendingRequests[cacheKey]) {
    console.log('Returning pending distance UOMs request');
    return pendingRequests[cacheKey];
  }

  try {
    // Make API request with type filter
    pendingRequests[cacheKey] = API.get(SENSOR_UOMS_URL, {
      params: { type: 'Length' },
      headers: getAuthHeaders(),
    })
      .then((response) => {
        const data = response.data as ILengthUOM[];

        // Update cache
        cache[cacheKey] = {
          data,
          timestamp: Date.now(),
        };

        // Clear the pending request reference
        delete pendingRequests[cacheKey];

        return data;
      })
      .catch((error) => {
        // Clear the pending request reference on error
        delete pendingRequests[cacheKey];
        console.error('Error fetching distance UOMs:', error);
        throw error;
      });

    return await pendingRequests[cacheKey];
  } catch (error) {
    console.error('Error fetching distance UOMs:', error);
    throw error;
  }
}

/**
 * Assign a tank configuration to a device
 * @param deviceSerial Serial number of the device
 * @param tankConfigId ID of the tank configuration to assign
 * @returns Promise resolving to success status
 */
export async function assignTankToDevice(deviceSerial: string, tankConfigId: number): Promise<boolean> {
  try {
    // Validate required parameters
    if (!deviceSerial) {
      throw new Error('Device serial is required');
    }
    if (!tankConfigId) {
      throw new Error('Tank configuration ID is required');
    }

    // Request payload using inline type definition
    const assignmentData = {
      device_serial: deviceSerial,
      tank_config_id: tankConfigId,
    };

    // Invalidate any cache that might be affected by this operation
    const deviceCacheKey = `device_${deviceSerial}`;
    delete cache[deviceCacheKey];

    // Make API request
    const response = await API.post(`${DEVICES_URL}/${deviceSerial}/assign-tank`, assignmentData, {
      headers: getAuthHeaders(),
    });

    // Return success based on response status
    return response.status >= 200 && response.status < 300;
  } catch (error) {
    console.error(`Error assigning tank to device ${deviceSerial}:`, error);
    // Type guard for Axios errors
    if (error && typeof error === 'object' && 'isAxiosError' in error) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        // Handle specific API error responses
        const statusCode = axiosError.response.status;
        const errorData = axiosError.response.data as { message?: string } | undefined;
        const errorMessage = errorData?.message || 'Unknown error';
        if (statusCode === 404) {
          throw new Error(`Device with serial ${deviceSerial} not found`);
        } else if (statusCode === 400) {
          throw new Error(`Invalid assignment request: ${errorMessage}`);
        }
      }
    }
    throw error;
  }
}

/**
 * Get required dimensions for a specific tank type
 * @param tankTypeId ID of the tank type
 * @returns Promise resolving to array of required dimensions
 */
export async function getTankTypeDimensions(tankTypeId: number): Promise<IContainerType['dimensions']> {
  const cacheKey = `tank_type_dimensions_${tankTypeId}`;
  const now = Date.now();

  // Return cached data if it's still valid
  if (cache[cacheKey] && now - cache[cacheKey].timestamp < CACHE_TTL) {
    console.log(`Returning cached dimensions for tank type ${tankTypeId}`);
    return cache[cacheKey].data as IContainerType['dimensions'];
  }

  // If there's already a pending request for this data, return that instead of making a new one
  if (pendingRequests[cacheKey]) {
    console.log(`Returning pending dimensions request for tank type ${tankTypeId}`);
    return pendingRequests[cacheKey];
  }

  try {
    // Make API request
    pendingRequests[cacheKey] = API.get(`${TANK_TYPES_URL}/${tankTypeId}/dimensions`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        const data = response.data as IContainerType['dimensions'];

        // Update cache
        cache[cacheKey] = {
          data,
          timestamp: Date.now(),
        };

        // Clear the pending request reference
        delete pendingRequests[cacheKey];

        return data;
      })
      .catch((error) => {
        // Clear the pending request reference on error
        delete pendingRequests[cacheKey];
        console.error(`Error fetching dimensions for tank type ${tankTypeId}:`, error);
        throw error;
      });

    return await pendingRequests[cacheKey];
  } catch (error) {
    console.error(`Error fetching dimensions for tank type ${tankTypeId}:`, error);
    throw error;
  }
}

// Tank volume calculator types
export interface ITankVolumeCalculatorRequest {
  shape?: string;
  dimensions: Record<string, number>;
  empty_distance?: number;
  input_unit?: string;
  output_unit?: string;
  tank_type_name: string;
}

export interface ITankVolumeCalculatorResponse {
  total_volume: number;
  filled_volume: number;
  fill_percentage: number;
  output_unit: string;
}

/**
 * Calculate tank volumes based on tank specifications without storing tank configurations
 * @param tankParams Tank parameters including shape, dimensions, empty distance, and units
 * @returns Promise resolving to calculated volumes and fill percentage
 */
export async function calculateTankVolume(
  tankParams: ITankVolumeCalculatorRequest,
): Promise<ITankVolumeCalculatorResponse> {
  try {
    // Validate required fields
    if (!tankParams.shape) {
      throw new Error('Tank shape is required');
    }
    if (!tankParams.dimensions || Object.keys(tankParams.dimensions).length === 0) {
      throw new Error('Tank dimensions are required');
    }

    // Set default values if not provided
    const requestData: ITankVolumeCalculatorRequest = {
      ...tankParams,
      empty_distance: tankParams.empty_distance ?? 0,
      input_unit: tankParams.input_unit ?? 'meters',
    };

    // Make API request
    const response = await API.post(TANK_VOLUMES_URL, requestData, {
      headers: getAuthHeaders(),
    });

    // Return volume calculation results
    return response.data as unknown as ITankVolumeCalculatorResponse;
  } catch (error) {
    console.error('Error calculating tank volume:', error);
    // Type guard for Axios errors
    if (error && typeof error === 'object' && 'isAxiosError' in error) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        // Handle specific API error responses
        const statusCode = axiosError.response.status;
        const errorData = axiosError.response.data as { message?: string } | undefined;
        const errorMessage = errorData?.message || 'Unknown error';
        if (statusCode === 400) {
          throw new Error(`Invalid tank volume calculation request: ${errorMessage}`);
        } else if (statusCode === 403) {
          throw new Error('You do not have permission to perform tank volume calculations');
        }
      }
    }
    throw error;
  }
}
